import { useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import editFill from '@iconify/icons-eva/edit-fill';
import { useNavigate } from 'react-router-dom';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import autoRenew from '@iconify/icons-ic/round-autorenew';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import copyContent from '@iconify/icons-eva/copy-fill';
// material
import { Menu, MenuItem, IconButton, ListItemIcon, ListItemText } from '@mui/material';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { ClubRemove } from '.';
import { createTokenClub, deleteItem, editItem } from '../../fetch';
import { groupsActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectGroupsTokenArray } from '../../createSelectorUtils';
// ----------------------------------------------------------------------

ClubMoreMenu.propTypes = {
  id: PropTypes.number.isRequired
};

export default function ClubMoreMenu({ id }) {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const token = useSelector(selectGroupsTokenArray).find((item) => item.groupId === id);

  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);

  const handleDelete = async () => {
    const response = await deleteItem('groups', id);

    if (response.ok) {
      enqueueSnackbar(t('CL_Snackbar_Delete'), { variant: 'success' });
      dispatch(groupsActions.delete(id));
      setIsOpen(false);
    } else {
      const error = await response.text();
      const message = error.slice(0, error.indexOf('-'));
      enqueueSnackbar(message, { variant: 'error' });
    }
    setOpenRemove(false);
  };

  const handleCopyToken = async () => {
    if (token !== undefined) {
      enqueueSnackbar(t('CL_Snackbar_Token_Copy'), { variant: 'success non' });
      setIsOpen(false);
    } else {
      enqueueSnackbar(t('CL_Snackbar_Token_Unexist'), { variant: 'warning' });
    }
  };

  const handleRenewToken = async () => {
    if (token !== undefined) {
      const response = await editItem('group/token', token.id, { id: token.id, groupId: id });
      if (response.ok) {
        enqueueSnackbar(t('CL_Snackbar_Token_Renew'), { variant: 'success' });
        dispatch(groupsActions.updateToken([await response.json()]));
        setIsOpen(false);
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
    } else {
      enqueueSnackbar(t('CL_Snackbar_Token_Unexist'), { variant: 'warning' });
    }
    setIsOpen(false);
  };
  const handleCreateToken = async () => {
    if (token !== undefined) {
      enqueueSnackbar(t('CL_Snackbar_Token_Exist'), { variant: 'warning' });
    } else {
      const response = await createTokenClub(id);

      if (response.ok) {
        enqueueSnackbar(t('CL_Snackbar_Token_Create'), { variant: 'success' });
        dispatch(groupsActions.updateToken([await response.json()]));
        setIsOpen(false);
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
      setIsOpen(false);
    }
  };
  const handleDeleteToken = () => {
    if (token !== undefined) {
      deleteItem('group/token', token.id)
        .then((response) => {
          if (response.ok) {
            enqueueSnackbar(t('CL_Snackbar_Token_Delete'), { variant: 'success' });
            dispatch(groupsActions.deleteToken(token.id));
            setIsOpen(false);
          } else {
            response.text().then((error) => {
              const message = error.slice(0, error.indexOf('-'));
              enqueueSnackbar(message, { variant: 'error' });
            });
          }
        })
        .finally(() => setIsOpen(false));
    } else {
      enqueueSnackbar(t('CL_Snackbar_Token_Unexist'), { variant: 'warning' });
    }
  };
  return (
    <>
      <IconButton ref={ref} onClick={() => setIsOpen(true)}>
        <Icon icon={moreVerticalFill} width={20} height={20} />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: { width: 200, maxWidth: '100%' }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => navigate('edit', { state: { id } })}
          sx={{ color: 'text.secondary' }}
        >
          <ListItemIcon>
            <Icon icon={editFill} width={24} height={24} />
          </ListItemIcon>
          <ListItemText primary={t('btn_Edit')} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        <CopyToClipboard text={token !== undefined ? token.token : ''} onCopy={handleCopyToken}>
          <MenuItem sx={{ color: 'text.secondary' }}>
            <ListItemIcon>
              <Icon icon={copyContent} width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={t('CL_Menu_CopyToken')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        </CopyToClipboard>

        <MenuItem sx={{ color: 'text.secondary' }} onClick={handleRenewToken}>
          <ListItemIcon>
            <Icon icon={autoRenew} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('CL_Menu_RenewToken')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <MenuItem sx={{ color: 'text.secondary' }} onClick={handleCreateToken}>
          <ListItemIcon>
            <Icon icon={plusFill} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('CL_Menu_CreateToken')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <MenuItem sx={{ color: 'text.secondary' }} onClick={handleDeleteToken}>
          <ListItemIcon>
            <Icon icon={trash2Outline} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('CL_Menu_DeleteToken')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <MenuItem sx={{ color: 'text.secondary' }} onClick={() => setOpenRemove(true)}>
          <ListItemIcon>
            <Icon icon={trash2Outline} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('CL_Menu_DeleteClub')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>
      </Menu>
      <ClubRemove
        open={openRemove}
        handleClose={() => setOpenRemove(false)}
        handleDelete={handleDelete}
      />
    </>
  );
}
